// The text of a Parliament record, presentational: the sections of the
// index in reading order, each with its heading (the title of a topic or an
// item; the speaker of a speech, with role and group under the name) and
// its paragraphs, one page at a time as the news reader does (the next page
// when the end enters the viewport, and always with «Continua a leggere»).
// «Vai al punto» (`target`) shows up to the entry's page, scrolls to it and
// moves the focus to its heading. What is shown never changes under the
// reader: pages are only added at the end. Every element is one the design
// already has:
//   section title  the title of a card, news/NewsCard.tsx:143
//   speaker        the channel and date lines of a card, news/NewsCard.tsx:135-138, without the avatar
//   paragraphs     the card's paragraphs, news/NewsCard.tsx:102-107
//   counter, more  the news reader's, news/NewsReader.tsx:132-180
// The data comes ready (readerPages): nothing here calls the api.
import { Button } from "@heroui/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { pageOfSection, sectionAnchorId, type ReaderPage } from "../../features/parliament/adapt";
import { CONTINUE_READING } from "../news/NewsReader";
import { STATUS_BUTTON_CLASS } from "../PanelStatus";

/** How far below the viewport the next page is asked for, as the news reader. */
const SENTINEL_MARGIN = "200px";
const integer = new Intl.NumberFormat("it-IT");

/** The entry «vai al punto» asks for; `nonce` tells one click from the next on the same entry. */
export interface ReaderTarget {
  ordinal: number;
  nonce: number;
}

type ParliamentReaderProps = {
  pages: ReaderPage[];
  /** The characters of every page together: «Mostrati N di <totalChars> caratteri». */
  totalChars: number;
  target: ReaderTarget | null;
};

export function ParliamentReader({ pages, totalChars, target }: ParliamentReaderProps) {
  // how many pages were opened, by reading on or by «vai al punto»: it only ever grows
  const [opened, setOpened] = useState(1);
  const sentinel = useRef<HTMLDivElement>(null);
  // the pages shown: the ones opened so far, and up to the target's page in the same render
  const targetPage = target ? pageOfSection(pages, target.ordinal) : -1;
  const shown = Math.min(Math.max(opened, targetPage + 1), Math.max(pages.length, 1));
  const hasMore = shown < pages.length;
  // the target's page counts as opened from now on, so a later target on an earlier page takes nothing
  // away: the state is adjusted while rendering (react.dev, «Adjusting some state when a prop changes»),
  // and once it holds `shown` the condition is false
  if (shown > opened) setOpened(shown);

  // the target's page is in the DOM in the same render: scroll to the entry and move the focus to its heading
  useLayoutEffect(() => {
    if (!target) return;
    const element = document.getElementById(sectionAnchorId(target.ordinal));
    if (!element) return;
    if (typeof element.scrollIntoView === "function") element.scrollIntoView({ block: "start" });
    const heading = element.querySelector<HTMLElement>("[data-reader-heading]");
    (heading ?? element).focus({ preventScroll: true });
  }, [target]);

  useEffect(() => {
    const element = sentinel.current;
    if (!hasMore || !element || typeof IntersectionObserver !== "function") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting))
          setOpened((o) => Math.max(o, Math.min(shown + 1, pages.length)));
      },
      { rootMargin: SENTINEL_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasMore, shown, pages.length]);

  const visible = pages.slice(0, shown);
  const shownChars = visible.reduce((sum, page) => sum + page.chars, 0);

  return (
    <section aria-label="Testo" className="flex flex-col gap-[15px]">
      <div className="flex flex-col gap-[15px]">
        {visible.flatMap((page) =>
          page.sections.map((section, i) => {
            const id = section.ordinal === null ? undefined : sectionAnchorId(section.ordinal);
            const key = section.ordinal === null ? `preamble-${i}` : `s-${section.ordinal}`;
            return (
              <section key={key} id={id} tabIndex={id ? -1 : undefined} className="flex flex-col gap-2 outline-none">
                {section.heading !== null && section.level === "speech" ? (
                  <h3 tabIndex={-1} data-reader-heading className="text-xs leading-5 font-medium outline-none">
                    <span className="block text-black">{section.heading}</span>
                    {section.speakerDetail && <span className="block text-muted">{section.speakerDetail}</span>}
                  </h3>
                ) : section.heading !== null ? (
                  <h3 tabIndex={-1} data-reader-heading className="text-sm leading-6 font-medium text-accent outline-none">
                    {section.heading}
                  </h3>
                ) : null}
                {section.blocks.length > 0 && (
                  <div className="text-xs leading-5 text-muted">
                    {section.blocks.map((block, b) => (
                      <p key={b} className="min-h-5">
                        {block}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            );
          }),
        )}
      </div>
      {pages.length > 1 && (
        <>
          <div ref={sentinel} aria-hidden />
          <p aria-live="polite" className="text-xs leading-5 text-muted">
            Mostrati {integer.format(shownChars)} di {integer.format(totalChars)} caratteri
          </p>
          {hasMore && (
            <div className="flex items-center justify-end">
              <Button
                size="sm"
                onPress={() => setOpened((o) => Math.max(o, shown + 1))}
                className={STATUS_BUTTON_CLASS}
              >
                {CONTINUE_READING}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
